export const accessoryComparisonSource = {
  workbook: "bobrick_asi_bradley_by_brand.xlsx",
  sheets: ["Bobrick", "ASI", "Bradley"],
  rowsPerSheet: 20,
} as const;

export const accessoryComparisonBrands = ["Bobrick", "ASI", "Bradley"] as const;

export type AccessoryComparisonBrand = (typeof accessoryComparisonBrands)[number];

export type AccessoryComparisonBrandVisual = {
  logo: string;
  logoAlt: string;
  logoWidth: number;
  logoHeight: number;
};

export const accessoryComparisonBrandVisuals: Record<
  AccessoryComparisonBrand,
  AccessoryComparisonBrandVisual
> = {
  Bobrick: {
    logo: "/manus-storage/bobrick-logo_a813f5b3.webp",
    logoAlt: "Bobrick logo",
    logoWidth: 200,
    logoHeight: 150,
  },
  ASI: {
    logo: "/manus-storage/asi-group-logo_77ed7f23.webp",
    logoAlt: "ASI Group logo",
    logoWidth: 200,
    logoHeight: 150,
  },
  Bradley: {
    logo: "/manus-storage/bradley-logo_b64719cc.webp",
    logoAlt: "Bradley logo",
    logoWidth: 200,
    logoHeight: 150,
  },
};

export const accessoryComparisonSelectionGuide = {
  eyebrow: "Three comparable options",
  title: "Lay out the options. Confirm the project fit. Quote with confidence.",
  copy: "Each board places Bobrick, ASI, and Bradley side by side for the same commercial restroom need. Start with the product type and model family, then confirm dimensions, mounting, finish, capacity, and project requirements before selecting a final specification.",
} as const;

export type AccessoryComparisonProductImage = {
  src: string;
  alt: string;
  width: number;
  height: number;
  provenanceKey: string;
  representative?: boolean;
  note?: string;
};

export type AccessoryComparisonOption = {
  brand: AccessoryComparisonBrand;
  model: string;
  product: string;
  details: string;
  qualification?: string;
  image?: AccessoryComparisonProductImage;
};

export type AccessoryComparisonGroup = {
  id: string;
  rank: number;
  title: string;
  comparisonNote: string;
  options: [AccessoryComparisonOption, AccessoryComparisonOption, AccessoryComparisonOption];
};

export type AccessoryComparisonSection = {
  id: string;
  routeGroupId: string;
  tileIds: string[];
  eyebrow: string;
  title: string;
  intro: string;
  groups: AccessoryComparisonGroup[];
};

export const accessoryComparisonSections: AccessoryComparisonSection[] = [
  {
    id: "accessory-compare-bath-shower",
    routeGroupId: "accessibility-safety",
    tileIds: ["bath-shower"],
    eyebrow: "Bath & shower comparisons",
    title: "Compare shower rods and folding shower seats.",
    intro: "Review comparable Bobrick, ASI, and Bradley product families, then confirm dimensions, mounting, substrate, finish, and project accessibility requirements before pricing.",
    groups: [
      {
        id: "heavy-duty-shower-curtain-rods",
        rank: 5,
        title: "Heavy-duty shower curtain rods",
        comparisonNote: "Comparable stainless shower-rod families; lengths, diameters, and flange details vary.",
        options: [
          { brand: "Bobrick", model: "B-6107 Series", product: "Heavy-Duty Shower Curtain Rod", details: "Type 304 stainless, 20-gauge, 1-inch diameter rod with 2-1/2-inch square flanges; available in 36- to 72-inch lengths." },
          { brand: "ASI", model: "1214-2 (with 1214-1 flanges)", product: "Heavy-Duty Shower Curtain Rod", details: "Type 304 stainless, 20-gauge, 1-inch diameter tubing with separate surface flanges; custom lengths available to 120 inches." },
          { brand: "Bradley", model: "9538/9539 Series", product: "Stainless Steel Shower Curtain Rod", details: "Satin stainless tubing in 1-inch or 1-1/4-inch outside diameters with a concealed-mounting design." },
        ],
      },
      {
        id: "concealed-shower-curtain-rods",
        rank: 13,
        title: "Concealed-mount shower curtain rods",
        comparisonNote: "Comparable concealed-mount rod families; confirm flange, length, diameter, and substrate requirements.",
        options: [
          { brand: "Bobrick", model: "B-207 Series", product: "Concealed-Mount Shower Curtain Rod", details: "Type 304 stainless, 1-inch diameter rod with concealed-mount hardware." },
          { brand: "ASI", model: "1214-2 (concealed option)", product: "Shower Curtain Rod", details: "The 1214 rod family can be coordinated with concealed or exposed mounting options; verify the required flange set." },
          { brand: "Bradley", model: "9538/9539 Series", product: "Concealed-Mount Shower Curtain Rod", details: "Satin stainless rod family with concealed mounting and 1-inch or 1-1/4-inch tubing options." },
        ],
      },
      {
        id: "folding-shower-seats",
        rank: 20,
        title: "Folding shower seats",
        comparisonNote: "Comparable folding-seat function; listed seat materials, dimensions, construction, and support details differ.",
        options: [
          { brand: "Bobrick", model: "Shower Seats (Family)", product: "Shower & Tub", details: "Bobrick shower and tub accessory family that includes folding shower-seat configurations; select the project-specific model and size." },
          { brand: "ASI", model: "8207", product: "Folding Shower Seat", details: "Type 304 stainless slatted seat, 18 inches wide, with a self-locking mechanism and published 500-pound support rating." },
          { brand: "Bradley", model: "9562/9591 Family", product: "Folding Phenolic Shower Seat", details: "Folding solid-white phenolic seat on a satin stainless frame with a published 500-pound static-load rating." },
        ],
      },
    ],
  },
  {
    id: "accessory-compare-grab-bars",
    routeGroupId: "accessibility-safety",
    tileIds: ["grab-bars"],
    eyebrow: "Grab-bar comparisons",
    title: "Compare concealed-mount grab-bar families.",
    intro: "Start with diameter and configuration, then verify length, backing, finish, load documentation, and project accessibility requirements.",
    groups: [
      {
        id: "grab-bars-one-and-one-half-inch",
        rank: 10,
        title: "1-1/2-inch grab bars",
        comparisonNote: "Comparable 1-1/2-inch concealed-mount families; published load ratings and finish options differ.",
        options: [
          { brand: "Bobrick", model: "B-6806 Series", product: "Grab Bars, 1-1/2-inch diameter", details: "18-gauge Type 304 stainless tubing with snap-flange concealed mounting; satin or peened finish options and a published 900-pound load rating." },
          { brand: "ASI", model: "3800/3801 Series", product: "Snap-Flange Grab Bar, 1-1/2-inch diameter", details: "18-gauge Type 304 stainless with snap-over flange covers; satin, peened, and selected powder-coated finishes." },
          { brand: "Bradley", model: "812 Series", product: "Grab Bar, 1-1/2-inch diameter", details: "18-gauge satin stainless with snap-over escutcheons; published standard and SteadyCare load options vary by configuration." },
        ],
      },
      {
        id: "grab-bars-one-and-one-quarter-inch",
        rank: 15,
        title: "1-1/4-inch grab bars",
        comparisonNote: "The workbook documents no exact mainstream Bradley 1-1/4-inch equivalent; Bradley 812 is 1-1/2 inches and must not be treated as an automatic substitute.",
        options: [
          { brand: "Bobrick", model: "B-5806 Series", product: "Grab Bars, 1-1/4-inch diameter", details: "18-gauge Type 304 stainless, 1-1/4-inch tubing with snap-flange concealed mounting." },
          { brand: "ASI", model: "3700/3701 Series", product: "Snap-Flange Grab Bar, 1-1/4-inch diameter", details: "18-gauge Type 304 stainless, 1-1/4-inch outside diameter with concealed snap-flange mounting." },
          { brand: "Bradley", model: "812 Series (1-1/2-inch only)", product: "Nearest listed Bradley grab-bar family", details: "Bradley’s listed 812 family is 1-1/2 inches in diameter, not an exact 1-1/4-inch match.", qualification: "No exact 1-1/4-inch Bradley match is documented in the workbook." },
        ],
      },
    ],
  },
  {
    id: "accessory-compare-paper-waste",
    routeGroupId: "paper-waste",
    tileIds: ["paper-towel-dispensers", "waste-receptacles-liners"],
    eyebrow: "Paper & waste comparisons",
    title: "Compare towel dispensers and combination waste units.",
    intro: "Use the model families as a shortlist, then confirm paper format, capacity, mounting depth, dispensing method, power, waste volume, and service access.",
    groups: [
      {
        id: "recessed-towel-waste-units",
        rank: 2,
        title: "Recessed towel dispenser and waste units",
        comparisonNote: "Comparable recessed combination units; published towel and waste capacities differ substantially.",
        options: [
          { brand: "Bobrick", model: "B-36903", product: "Combination Towel/Waste Unit (Recessed)", details: "TrimLine recessed satin-stainless unit with TowelMate, 300 C-fold or 400 multifold towel capacity, and a 1.6-gallon removable waste container." },
          { brand: "ASI", model: "0469", product: "Paper Towel Dispenser & Waste Receptacle (Recessed)", details: "Recessed stainless unit for 600 C-fold or 800 multifold towels with a removable 12-gallon waste receptacle." },
          { brand: "Bradley", model: "234 Series (234-00)", product: "Towel Dispenser/Waste Receptacle, 12-Gallon (Recessed)", details: "Recessed satin-stainless unit for 800 multifold or 600 C-fold towels with a removable 12-gallon waste receptacle." },
        ],
      },
      {
        id: "automatic-towel-waste-units",
        rank: 7,
        title: "Automatic towel and waste units",
        comparisonNote: "Comparable combination-unit families; verify dispensing method, power, mounting, and capacity because the listed ASI and Bradley rows represent broader families.",
        options: [
          { brand: "Bobrick", model: "B-3974", product: "Combination Towel/Waste Unit (Recessed)", details: "ClassicSeries recessed automatic combination dispenser and waste receptacle in stainless steel." },
          { brand: "ASI", model: "0469", product: "Paper Towel Dispenser & Waste Receptacle (Recessed)", details: "Recessed combination unit with 600 C-fold or 800 multifold towel capacity and a 12-gallon waste receptacle; confirm dispensing method for the project." },
          { brand: "Bradley", model: "234 Series (234-00)", product: "Towel Dispenser/Waste Receptacle", details: "12-gallon combination-unit family with C-fold and multifold towel capacity; confirm the required dispensing configuration." },
        ],
      },
      {
        id: "folded-paper-towel-dispensers",
        rank: 8,
        title: "C-fold and multifold towel dispensers",
        comparisonNote: "Comparable folded-towel dispenser category; stated capacities and mounting options differ.",
        options: [
          { brand: "Bobrick", model: "Paper Towel Dispensers (Family)", product: "Paper Towel Dispensers", details: "Bobrick family covering surface, recessed, and semi-recessed C-fold, multifold, and roll formats; select a project-specific model." },
          { brand: "ASI", model: "0210", product: "Traditional Paper Towel Dispenser", details: "Surface-mounted satin Type 304 stainless dispenser for 400 C-fold or 525 multifold towels." },
          { brand: "Bradley", model: "252 Series", product: "Surface-Mounted Towel Dispenser", details: "Surface-mounted satin-stainless dispenser for 350 multifold or 200 C-fold towels with a refill indicator." },
        ],
      },
      {
        id: "towel-waste-families",
        rank: 9,
        title: "Towel dispenser and waste receptacle families",
        comparisonNote: "Comparable combination families; mounting variants, dispensing type, capacity, and wall depth must be verified.",
        options: [
          { brand: "Bobrick", model: "B-39617", product: "Combination Towel/Waste Unit", details: "Stainless combination paper towel dispenser and waste receptacle family; verify the required mounting and dispensing configuration." },
          { brand: "ASI", model: "0469", product: "Paper Towel Dispenser & Waste Receptacle", details: "Combination family with recessed, semi-recessed, and surface variants; the listed recessed configuration holds 600 C-fold or 800 multifold towels and 12 gallons of waste." },
          { brand: "Bradley", model: "234 Series", product: "Towel Dispenser/Waste Receptacle", details: "Combination family with multiple mounting options; verify the correct configuration, towel format, and waste capacity." },
        ],
      },
    ],
  },
  {
    id: "accessory-compare-sanitary-products",
    routeGroupId: "compartment-accessories",
    tileIds: ["sanitary-product-vendors-disposals"],
    eyebrow: "Sanitary product comparisons",
    title: "Compare free-vend dispensers and surface disposals.",
    intro: "Confirm the requested function, product capacity, free-vend requirement, mounting, compartment clearance, service access, and finish before pricing.",
    groups: [
      {
        id: "free-vend-napkin-tampon-dispensers",
        rank: 6,
        title: "Free-vend napkin and tampon dispensers",
        comparisonNote: "Comparable free-vend dual-product units; product capacities, cabinet construction, and operating mechanisms differ.",
        options: [
          { brand: "Bobrick", model: "B-47069C", product: "Napkin/Tampon Dispenser (Free Vend)", details: "Surface-mounted satin-stainless unit with push-button operation, holding 30 tampons and 20 napkins." },
          { brand: "ASI", model: "0864-F", product: "Napkin/Tampon Vendor (Free Vend)", details: "Surface-mounted dual vendor with free-vend capability, holding 27 tampons and 30 napkins." },
          { brand: "Bradley", model: "407 Series (407-114000)", product: "Napkin and Tampon Vendor (Free Vend)", details: "Surface-mounted frameless stainless vendor holding 28 tampons and 18 napkins with free-vend operation." },
        ],
      },
      {
        id: "sanitary-napkin-disposals",
        rank: 18,
        title: "Surface-mounted sanitary napkin disposals",
        comparisonNote: "Comparable surface disposals; published capacities, lid construction, and liner requirements differ.",
        options: [
          { brand: "Bobrick", model: "B-270", product: "Sanitary Napkin Disposal", details: "Surface-mounted satin-stainless disposal with a one-piece hinged cover and 1.0-gallon capacity." },
          { brand: "ASI", model: "0852", product: "Sanitary Napkin Disposal", details: "Surface-mounted Type 304 satin-stainless disposal with a top-hinged cover and 1.2-gallon capacity." },
          { brand: "Bradley", model: "4781-11", product: "Satin Finish Surface-Mounted Napkin Disposal", details: "Surface-mounted satin-stainless disposal with a piano-hinge flip top and 1.5-gallon capacity." },
        ],
      },
    ],
  },
  {
    id: "accessory-compare-toilet-tissue",
    routeGroupId: "compartment-accessories",
    tileIds: ["toilet-paper-dispensers"],
    eyebrow: "Toilet tissue comparisons",
    title: "Compare multi-roll toilet tissue dispensers.",
    intro: "Confirm roll size, roll count, partition or wall mounting, service side, theft resistance, projection, and compartment clearance.",
    groups: [
      {
        id: "multi-roll-toilet-tissue-dispensers",
        rank: 14,
        title: "Multi-roll toilet tissue dispensers",
        comparisonNote: "Comparable multi-roll category; the listed roll count, mounting configuration, and operating method differ.",
        options: [
          { brand: "Bobrick", model: "Toilet Tissue Dispensers (Family)", product: "Toilet Tissue Dispensers", details: "Bobrick family covering single, dual, and jumbo-roll surface and recessed formats; select a project-specific model." },
          { brand: "ASI", model: "0032", product: "Twin Hide-A-Roll Partition Dispenser", details: "Partition-mounted dispenser serving two stalls, holding four standard rolls for 1/2- to 1-1/4-inch partitions." },
          { brand: "Bradley", model: "5402 Series", product: "Dual-Roll Toilet Tissue Dispenser", details: "Surface-mounted satin-stainless dispenser for two standard-core rolls with automatic reserve-roll access." },
        ],
      },
    ],
  },
  {
    id: "accessory-compare-seat-covers",
    routeGroupId: "compartment-accessories",
    tileIds: ["toilet-seat-cover-dispensers"],
    eyebrow: "Seat-cover comparisons",
    title: "Compare surface-mounted seat-cover dispensers.",
    intro: "Confirm refill format, capacity, mounting location, projection, finish, and service access before pricing.",
    groups: [
      {
        id: "surface-seat-cover-dispensers",
        rank: 19,
        title: "Surface-mounted toilet seat-cover dispensers",
        comparisonNote: "Comparable surface-mounted function; Bradley’s listed capacity is higher than the Bobrick and ASI models.",
        options: [
          { brand: "Bobrick", model: "B-221", product: "Toilet Seat-Cover Dispenser", details: "Surface-mounted satin-stainless dispenser holding 250 single- or half-fold covers with bottom filling." },
          { brand: "ASI", model: "0477-SM", product: "Toilet Seat-Cover Dispenser", details: "Surface-mounted satin Type 304 stainless dispenser holding 250 single- or half-fold covers." },
          { brand: "Bradley", model: "583", product: "High-Capacity Surface-Mounted Seat Cover Dispenser", details: "Surface-mounted satin-stainless dispenser holding 500 single- or half-fold covers with a locked hinged bottom." },
        ],
      },
    ],
  },
  {
    id: "accessory-compare-soap-dispensers",
    routeGroupId: "handwashing-hygiene",
    tileIds: ["soap-dispensers"],
    eyebrow: "Soap-dispenser comparisons",
    title: "Compare 40-ounce wall-mounted soap dispensers.",
    intro: "Confirm liquid compatibility, orientation, refill access, valve, mounting, projection, finish, and facility maintenance preference.",
    groups: [
      {
        id: "forty-ounce-wall-soap-dispensers",
        rank: 17,
        title: "40-ounce wall-mounted soap dispensers",
        comparisonNote: "Comparable capacity and wall-mounted function; Bobrick and ASI are vertical while the listed Bradley model uses a horizontal tank.",
        options: [
          { brand: "Bobrick", model: "B-2111", product: "Vertical Soap Dispenser", details: "Surface-mounted satin-stainless vertical dispenser with 40-fluid-ounce capacity and a keyed hinged filler top." },
          { brand: "ASI", model: "0347", product: "Vertical Liquid Soap Dispenser", details: "Surface-mounted satin Type 304 stainless vertical dispenser with 40-fluid-ounce capacity and keyed refill access." },
          { brand: "Bradley", model: "6542", product: "Wall-Mounted Horizontal Tank Soap Dispenser", details: "Wall-mounted satin-stainless horizontal tank with 40-ounce capacity, chrome-plated brass valve, and keyed filler door." },
        ],
      },
    ],
  },
  {
    id: "accessory-compare-hooks",
    routeGroupId: "mirrors-storage",
    tileIds: ["hooks-shelves-racks"],
    eyebrow: "Hook comparisons",
    title: "Compare concealed and compact single robe hooks.",
    intro: "Confirm projection, mounting method, base geometry, material, finish, wall substrate, and expected use before pricing.",
    groups: [
      {
        id: "concealed-single-robe-hooks",
        rank: 12,
        title: "Concealed-mount single robe hooks",
        comparisonNote: "Comparable single-hook function; geometry, material, projection, and base shape differ.",
        options: [
          { brand: "Bobrick", model: "B-6717", product: "Surface-Mounted Single Robe Hook", details: "Satin-stainless square-design hook with a 2-by-2-inch flange and 2-inch wall projection." },
          { brand: "ASI", model: "0751", product: "Heavy-Duty Robe Hook", details: "Concealed-mount satin-chrome-plated brass hook with a square safety plate and 1-1/4-inch projection." },
          { brand: "Bradley", model: "9114", product: "Stainless Steel Single Robe Hook", details: "Concealed-mount stainless single hook with a round base and modern architectural profile." },
        ],
      },
      {
        id: "compact-single-robe-hooks",
        rank: 16,
        title: "Compact single robe hooks",
        comparisonNote: "Comparable compact single-hook function; mounting, geometry, projection, and load documentation differ.",
        options: [
          { brand: "Bobrick", model: "B-76717", product: "Compact Single Robe Hook", details: "Compact satin-stainless hook with a 2-by-2-inch flange and narrow hook body." },
          { brand: "ASI", model: "0751-A", product: "Heavy-Duty Robe Hook", details: "Exposed three-screw satin-chrome-plated brass hook with 3-1/4-inch projection." },
          { brand: "Bradley", model: "9114", product: "Stainless Steel Single Robe Hook", details: "Concealed-mount stainless single hook; the workbook identifies it as the closer Bradley option for compact scale." },
        ],
      },
    ],
  },
  {
    id: "accessory-compare-mirrors",
    routeGroupId: "mirrors-storage",
    tileIds: ["mirrors"],
    eyebrow: "Mirror comparisons",
    title: "Compare channel-frame, LED, and angle-frame mirrors.",
    intro: "Choose the mirror style first, then confirm exact width, height, frame profile, glass, mounting, lighting, electrical requirements, and project environment.",
    groups: [
      {
        id: "channel-frame-mirrors",
        rank: 1,
        title: "Channel-frame mirrors",
        comparisonNote: "Like-for-like channel-frame mirror families; confirm required dimensions, frame material, finish, glass, and mounting.",
        options: [
          { brand: "Bobrick", model: "B-165 Series", product: "Channel-Frame Mirrors", details: "One-piece Type 430 stainless channel frame with mitered corners, bright-polished finish, float glass, and sizes from 18 by 24 to 24 by 60 inches." },
          { brand: "ASI", model: "0620 Series", product: "Stainless Steel Chan-Lok Channel-Frame Mirror", details: "One-piece roll-formed 20-gauge Type 304 satin-stainless channel frame with tight mitered corners and plate glass in multiple sizes." },
          { brand: "Bradley", model: "781 Series", product: "Channel Frame Mirror", details: "One-piece roll-formed channel frame with mitered corners and first-quality 1/4-inch float glass." },
        ],
      },
      {
        id: "led-backlit-mirrors",
        rank: 3,
        title: "LED backlit mirrors",
        comparisonNote: "Comparable LED mirror families; dimensions, mounting, lighting output, controls, electrical requirements, and frame details vary.",
        options: [
          { brand: "Bobrick", model: "B-167 Series", product: "LED Backlit Mirrors", details: "Outer-etched LED mirror with IP-rated components, a 50,000-hour LED engine, safety-film glass, aluminum frame, and wall or recessed installation." },
          { brand: "ASI", model: "0640", product: "Frameless Mirror with LED Backlight", details: "Polished-edge frameless mirror with integral LED halo lighting, hardwired power, wall-switch control, and horizontal or vertical mounting." },
          { brand: "Bradley", model: "Elvari LED Mirrors", product: "LED Backlit Mirror", details: "Radiused-corner LED mirror family with optional LED shelf and vertical or horizontal mounting." },
        ],
      },
      {
        id: "commercial-mirror-families",
        rank: 4,
        title: "Commercial mirror families",
        comparisonNote: "Brand-family overview, not one exact model substitution; narrow by frame style, glass, size, mounting, and lighting first.",
        options: [
          { brand: "Bobrick", model: "Commercial Mirrors (Family)", product: "Mirrors", details: "Bobrick family including channel-frame, welded or angle-frame, LED-backlit, and tilt mirrors." },
          { brand: "ASI", model: "Mirrors (Category)", product: "Mirrors", details: "ASI family including 0620 channel-frame, 0600 angle-frame, 20650 curved-frame, and 0640 LED series." },
          { brand: "Bradley", model: "Mirrors (Category)", product: "Mirrors", details: "Bradley family including 780 angle-frame, 781 channel-frame, Elvari frame, frameless and LED, plus specialty security mirrors." },
        ],
      },
      {
        id: "angle-welded-frame-mirrors",
        rank: 11,
        title: "Angle- and welded-frame mirrors",
        comparisonNote: "Comparable rigid stainless mirror-frame families; verify frame profile, finish, glass thickness, dimensions, and mounting system.",
        options: [
          { brand: "Bobrick", model: "B-290 Series", product: "Welded-Frame Mirrors", details: "Satin stainless welded-frame mirror with plate glass." },
          { brand: "ASI", model: "0600 Series", product: "Inter-Lok Angle-Frame Mirror", details: "18-gauge Type 304 satin-stainless angle frame with welded corners and a concealed hanging bracket." },
          { brand: "Bradley", model: "780 Series", product: "Angle Frame Mirror", details: "Roll-formed stainless angle frame with welded, ground, and polished corners plus theft-resistant concealed mounting." },
        ],
      },
    ],
  },
];

export const accessoryComparisonAnchorByTileId: Readonly<Record<string, string>> = Object.fromEntries(
  accessoryComparisonSections.flatMap((section) => section.tileIds.map((tileId) => [tileId, section.id])),
);

export function getAccessoryComparisonSectionsForGroup(routeGroupId: string) {
  return accessoryComparisonSections.filter((section) => section.routeGroupId === routeGroupId);
}

export const accessoryComparisonCount = accessoryComparisonSections.reduce(
  (total, section) => total + section.groups.length,
  0,
);
